import { Box, Button, ButtonProps, FormHelperText, Grid, Drawer, 
  TextField as MuiTextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useFormik, useFormikContext } from "formik";
import { useSaveContext } from "ra-core";
import { Dispatch, FC, useReducer } from "react";
import * as Yup from 'yup'
import Create from "@/controller/Create";
import CreateOrEdit from "@/controller/CreateOrEdit";
import { useDefaultSavedHandlers } from "@/controller/defaultSavedHandlers";
import { useSimpleSaveAction } from "@/controller/SaveContextProvider";
import { SupportedResources } from "@/dataProvider/tools";
import { useResourceRegister } from "@/utils/resourceRegister";
import retrieveAxiosErr from "@/utils/retrieveAxiosErr";
import OdForm from "../formik/OdForm";
import TextField from "../formik/TextField";
import { reducer, RequestRecipt, States, Action, useSendCodeCountDown } from "./ChangePhone.hook";


export type ChangePhoneProps = ButtonProps & {
  acceptNewPhone(newPhone: string): void;
}

const validationSchema = 
Yup.object().shape({
  phone: Yup.string().max(50)
  .matches(/^[\+]?[1-9]\d{1,14}$/, 'Invalid phone number')
  .required('Change Phone Number is required')
})

export const ChangePhone: FC<ChangePhoneProps> = (props) => {
  const resource: SupportedResources = 'phone-veri-code'
  useResourceRegister({ name: resource })
  
  const { acceptNewPhone, ...rest } = props

  const [states, dispatch] = useReducer(reducer, {
    requested: null as RequestRecipt | null,
    verified: false,
    vCode: '',
    open: false,
  })
  const countDownCtrl = useSendCodeCountDown(states, dispatch)

  const handleOpen  = () => dispatch({kind: 'open'});
  const handleClose = () => dispatch({kind: 'close'});

  const match = useMediaQuery('(max-width: 530px)')
  const width = match ? 'calc(100vw - 50px)' : 480

  const initialValues = {
    phone: '',
    submit: null,
  }
  
  const onSubmit = () => {}
  const formikCtrl = useFormik({ initialValues, onSubmit, validationSchema, enableReinitialize: true })
  const { errors } = formikCtrl

  const createHandlers = useDefaultSavedHandlers("Send code")
  const updateHandlers = useDefaultSavedHandlers("Phone update")
  return (
    <>
      <Button type="button" onClick={handleOpen} {...rest} >
        Change
      </Button>
      <Drawer
        anchor="right"
        onClose={handleClose}
        open={states.open}
        PaperProps={{ sx: { p: 2, width: width, } }}
      >
        <OdForm formikCtrl={formikCtrl}>
          <Typography color="textPrimary" variant="h6" >
            Change Phone Number
          </Typography>
          <Box sx={{ mt: 3 }}>
            <Grid container spacing={4}>
              <Grid item xs={12} >
                <Typography color="textSecondary" variant="body2" sx={{ mb: 3 }} >
                  Please enter your new mobile phone number below 
                  to receive a verification code via SMS for validation.
                </Typography>
              </Grid>
              <Grid item xs={12} >
                <TextField
                  disabled={!!states.requested}
                  label="New Phone Number*"
                  name="phone"
                />
              </Grid>
              
              <Box sx={{ flex: '100%', pl: 4, pt: 3, '& button': { mt: 1, } }} >
                <Create resource={resource} basePath="" {...createHandlers}>
                  <SendCodeButton states={states} countDownCtrl={countDownCtrl} />
                </Create>
              </Box>
              
              {states.requested ?
                <>
                <Grid item xs={12} >
                  <Typography color="textSecondary" variant="body2" >
                    Please enter the verification code received via SMS.
                  </Typography>
                </Grid>
                <Grid item xs={12} >
                  <MuiTextField
                    id="vcode"
                    fullWidth
                    label="Verification Code"
                    name="vcode"
                    onChange={(e) => dispatch(({kind:'vCode', vCode: e.target.value}))}
                    value={states.vCode}
                    variant="outlined"
                  />
                </Grid>
                </>
                : null
              }
            </Grid>
          {errors.submit && (
            <Box sx={{ mt: 3 }}>
              <FormHelperText error>
                {errors.submit}
              </FormHelperText>
            </Box>
          )}
          </Box>
          {!!states.vCode &&
            <Box sx={{ mt: 3 }}>
              <CreateOrEdit resource={resource} basePath="" record={ states.requested } {...updateHandlers}>
                <SavePhoneUpdateButton states={states} dispatch={dispatch} acceptNewPhone={acceptNewPhone} clearCountDown={countDownCtrl.clearCountDown} />
              </CreateOrEdit>
            </Box>}
        </OdForm>
      </Drawer>
    </>
  )
}

export default ChangePhone;


const SendCodeButton : FC<{states: States, countDownCtrl: ReturnType<typeof useSendCodeCountDown>}> = ({
  states, countDownCtrl,
}) => {
  const theme = useTheme()

  const {
    states: {innerStates},
    clearCountDown,
    onRequestCodeSuccess,
  } = countDownCtrl

  const { saving } = useSaveContext()
  const requestCode = useSimpleSaveAction()

  const {isSubmitting, values, errors, setFieldValue, setFieldError} = useFormikContext<{ phone: string; submit: null; }>()

  return (
    <>
    <Button
      sx={{ mr: 2 }}
      color={theme.palette.mode === 'light' ? "inherit" : 'primary'}
      variant={theme.palette.mode === 'light' ? "contained" : 'text'}
      disabled={isSubmitting || !values.phone || !!errors.phone || saving || innerStates.countDown !== null}
      type="button"
      onClick={() => {
        if (states.requested && (Date.now() - states.requested.sentAt.getTime() < 60000)) { return }
        requestCode({phone: values.phone} as any, { 
          onSuccess: onRequestCodeSuccess(values.phone),
          onFailure: (err:any) => setFieldError('submit', retrieveAxiosErr(err)),
        })
      }}
    >
      {!states.requested ? "Send Code" : 
        (innerStates.countDown === null ? "Resend Code" : `Resend in ${innerStates.countDown}s`)}
    </Button>
    {!!states.requested &&
      <Button
        color={theme.palette.mode === 'light' ? "inherit" : 'primary'}
        variant={theme.palette.mode === 'light' ? "contained" : 'text'}
        disabled={isSubmitting || !states.requested || !!errors.phone}
        type="button"
        onClick={() => {
          setFieldValue('phone', '')
          clearCountDown()
        }}
      >
        Change Phone Number
      </Button>
    }
    </>
  )
}

const SavePhoneUpdateButton : FC<{states: States, dispatch: Dispatch<Action>, acceptNewPhone:(newPhone: string)=>void, clearCountDown:()=>void}> = ({
  states, dispatch, acceptNewPhone, clearCountDown,
}) => {
  const sendConfirm = useSimpleSaveAction()
  const { setFieldError, resetForm } = useFormikContext()

  const handleSave = () => {
    if (states.requested) {
      const req = states.requested
      sendConfirm({
        id: req.vCodeId,
        verificatoinCodeId: req.vCodeId,
        code: states.vCode,
      }, { 
        onSuccess: () => {
          acceptNewPhone(req.phone)
          clearCountDown()
          dispatch({kind: 'resetRequested'})
          dispatch({kind: 'clearVCode'})
          resetForm()
        },
        onFailure: (err:any) => setFieldError('submit', retrieveAxiosErr(err)),
      })
    }
  }

  return (
    <Button
      color="primary"
      fullWidth
      onClick={() => handleSave()}
      variant="contained"
    >
      Update
    </Button>
  )
}
